// UTM attribution for outbound App Store CTAs.
//
// captureAttribution() runs once at startup (from main.jsx) and records the
// visitor's true inbound origin — from the landing URL's UTMs, or inferred from
// the referrer, falling back to accounteditor.com/referral for direct visits.
//
// buildCtaUrl(base, buttonLabel) stamps that origin onto an outbound link, plus
// utm_content = "<page-slug>::<button-label>" so we can see which CTA on which
// page was clicked. The Shopify App Store only honours the 5 standard UTM
// params, so the page+button detail is packed into utm_content.
//
// EVERYTHING here is wrapped so a blocked/unavailable Web Storage API (Safari
// private mode, sandboxed iframes, storage-disabled browsers) can never throw
// into the app: capture is a no-op on failure and buildCtaUrl falls back to the
// bare base URL. There is also an in-memory copy so attribution still works for
// the current page even when storage writes are rejected.

const FIRST_TOUCH_KEY = 'ae:attr:first'; // localStorage — persists across visits
const LAST_TOUCH_KEY = 'ae:attr:last'; // sessionStorage — this visit

const DEFAULT_ATTR = { source: 'accounteditor.com', medium: 'referral', campaign: 'none' };

// In-memory fallback so a single page view works even if storage is blocked.
let memoryAttr = null;

const SEARCH_ENGINES = ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'baidu', 'yandex'];
const SOCIAL = ['facebook', 'instagram', 'twitter', 't.co', 'x.com', 'linkedin', 'lnkd.in', 'youtube', 'pinterest', 'reddit', 'tiktok'];

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // storage unavailable / quota — ignore, we still have memoryAttr
  }
}

// Read utm_source/medium/campaign from the current URL, if present.
function fromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const source = p.get('utm_source');
    if (!source) return null;
    return {
      source,
      medium: p.get('utm_medium') || '(not set)',
      campaign: p.get('utm_campaign') || '(not set)',
    };
  } catch {
    return null;
  }
}

// Google Ads auto-tagging sends a click id (gclid / gbraid / wbraid) instead of
// UTMs. When one is present and there are no explicit UTMs, record the paid
// Google source so the campaign isn't lost. Campaign is left unset — GA4/Ads
// backfill it server-side via the gclid link.
function fromClickId() {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('gclid') || p.get('gbraid') || p.get('wbraid')) {
      return { source: 'google', medium: 'cpc', campaign: '(not set)' };
    }
    return null;
  } catch {
    return null;
  }
}

// Infer origin from document.referrer when there are no inbound UTMs.
function fromReferrer() {
  try {
    const ref = document.referrer;
    if (!ref) return { ...DEFAULT_ATTR };

    const refHost = new URL(ref).hostname.replace(/^www\./, '');
    const ownHost = window.location.hostname.replace(/^www\./, '');

    // Same-site referrer (internal navigation / reload) — treat as direct.
    if (refHost === ownHost) return { ...DEFAULT_ATTR };

    const engine = SEARCH_ENGINES.find((s) => refHost.includes(s));
    if (engine) return { source: engine, medium: 'organic', campaign: '(not set)' };

    if (SOCIAL.some((s) => refHost.includes(s))) {
      return { source: refHost, medium: 'social', campaign: '(not set)' };
    }

    return { source: refHost, medium: 'referral', campaign: '(not set)' };
  } catch {
    return { ...DEFAULT_ATTR };
  }
}

function isInternalReferral() {
  try {
    const ref = document.referrer;
    if (!ref) return false;
    const refHost = new URL(ref).hostname.replace(/^www\./, '');
    return refHost === window.location.hostname.replace(/^www\./, '');
  } catch {
    return false;
  }
}

export function captureAttribution() {
  try {
    if (typeof window === 'undefined') return;

    // Every internal link is a full page load, so a page view referred by our
    // own site carries no new origin. Keep the one this visit already recorded,
    // or a Google Ads visitor turns into accounteditor.com/referral one click in.
    const explicit = fromUrl() || fromClickId();
    if (!explicit && isInternalReferral()) {
      const kept = readStored(window.sessionStorage, LAST_TOUCH_KEY);
      if (kept) {
        memoryAttr = kept;
        return;
      }
    }

    // Priority: explicit UTMs → Google Ads click id → referrer inference.
    const attr = explicit || fromReferrer();
    memoryAttr = attr;

    const serialized = JSON.stringify(attr);
    // Last-touch: always the most recent origin for this session.
    safeSet(window.sessionStorage, LAST_TOUCH_KEY, serialized);
    // First-touch: only set once, ever.
    if (!safeGet(window.localStorage, FIRST_TOUCH_KEY)) {
      safeSet(window.localStorage, FIRST_TOUCH_KEY, serialized);
    }
  } catch {
    // Never let attribution capture break app startup.
  }
}

function readStored(storage, key) {
  const raw = safeGet(storage, key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.source) return parsed;
  } catch {
    // ignore malformed value
  }
  return null;
}

function getAttribution() {
  if (typeof window === 'undefined') return { ...DEFAULT_ATTR };
  return (
    readStored(window.sessionStorage, LAST_TOUCH_KEY) ||
    readStored(window.localStorage, FIRST_TOUCH_KEY) ||
    memoryAttr ||
    { ...DEFAULT_ATTR }
  );
}

// Turn the current pathname into a compact slug: '/' -> 'home',
// '/pricing' -> 'pricing', '/features/self-serve-edits' -> 'features-self-serve-edits'.
function pageSlug() {
  try {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    return path ? path.replace(/\//g, '-') : 'home';
  } catch {
    return 'home';
  }
}

export function buildCtaUrl(base, buttonLabel) {
  try {
    const attr = getAttribution();
    const url = new URL(base, window.location.origin);
    url.searchParams.set('utm_source', attr.source);
    url.searchParams.set('utm_medium', attr.medium);
    url.searchParams.set('utm_campaign', attr.campaign);
    url.searchParams.set('utm_content', `${pageSlug()}::${buttonLabel}`);
    return url.toString();
  } catch {
    // Never break a CTA — fall back to the untracked base link.
    return base;
  }
}

// ---------------------------------------------------------------------------
// Calendly
//
// Demo bookings used to leave the site with no attribution at all: all three
// entry points handed Calendly the bare CALENDLY_URL, so a booked call could
// not be traced to a campaign, page or CTA — while 19 App Store CTAs carried
// full UTMs. These helpers close that gap.
//
// Calendly accepts utm_source/medium/campaign/content/term plus `name` and
// `email` prefill on the scheduling URL, and the equivalent `utm` object on the
// embed widget. Values land in Meetings, webhook payloads and the v2 API.
// ---------------------------------------------------------------------------

// Calendly truncates UTM values past 255 characters, which would silently cut a
// value in half rather than reject it. Clamp short of the limit ourselves.
const UTM_MAX = 255;
const clamp = (v) => String(v ?? '').slice(0, UTM_MAX);

// '1,200 - 5,000 orders' -> '1200-5000'; free-text "Other" answers are slugged.
// Order volume rides in utm_term so a booking arrives pre-qualified by store
// size. It deliberately does NOT use Calendly's a1..a10 custom-answer slots:
// those depend on how the event's questions are configured, and guessing an
// index would prefill the wrong field.
function volumeTerm(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  // Only trust numeric extraction for a real range (the dropdown bands always
  // carry two numbers). A single number is ambiguous — "about 45k / month"
  // would yield "45" and report a 45,000-order store as 45 — so free text is
  // slugged verbatim instead, which is at least honest.
  const nums = v.replace(/,/g, '').match(/\d+/g);
  if (nums && nums.length >= 2) return `${nums[0]}-${nums[1]}`;
  return v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// The widget form of the same data, for Calendly.initInlineWidget({ utm }).
export function calendlyUtm(entryPoint, orderVolume) {
  try {
    const attr = getAttribution();
    const utm = {
      utmSource: clamp(attr.source),
      utmMedium: clamp(attr.medium),
      utmCampaign: clamp(attr.campaign),
      utmContent: clamp(`${pageSlug()}::${entryPoint}`),
    };
    const term = volumeTerm(orderVolume);
    if (term) utm.utmTerm = clamp(term);
    return utm;
  } catch {
    return {};
  }
}

// The redirect form, for `window.location.href = ...`. `name`/`email` prefill
// the Calendly booking screen so the visitor is not asked twice for details
// they just typed into our own form.
export function buildCalendlyUrl(base, { entryPoint, name, email, orderVolume } = {}) {
  try {
    const u = calendlyUtm(entryPoint, orderVolume);
    const url = new URL(base);
    if (u.utmSource) url.searchParams.set('utm_source', u.utmSource);
    if (u.utmMedium) url.searchParams.set('utm_medium', u.utmMedium);
    if (u.utmCampaign) url.searchParams.set('utm_campaign', u.utmCampaign);
    if (u.utmContent) url.searchParams.set('utm_content', u.utmContent);
    if (u.utmTerm) url.searchParams.set('utm_term', u.utmTerm);
    if (name) url.searchParams.set('name', String(name).slice(0, UTM_MAX));
    if (email) url.searchParams.set('email', String(email).slice(0, UTM_MAX));
    return url.toString();
  } catch {
    // Never break the booking flow — fall back to the untracked link.
    return base;
  }
}

// Flat attribution for analytics events and the lead-notification email, so a
// demo request is traceable even if the visitor never completes the Calendly
// booking. Keys match the form-field casing used elsewhere in the payload.
export function attributionFields() {
  try {
    const attr = getAttribution();
    return {
      Source: attr.source,
      Medium: attr.medium,
      Campaign: attr.campaign,
      'Landing Page': pageSlug(),
    };
  } catch {
    return {};
  }
}

export function attributionParams() {
  try {
    const attr = getAttribution();
    return { utm_source: attr.source, utm_medium: attr.medium, utm_campaign: attr.campaign };
  } catch {
    return {};
  }
}
